import AddIcon from "@mui/icons-material/Add";
import * as MUI from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";
import PageContentWrapper from "../../../../../components/wrappers/PageContentWrapper";
import { GlobalDataContext } from "../../../../../contexts/GlobalData";
import { useWarehouseLabelBuffer } from "../../../../../contexts/WarehouseLabelBuffer";
import { useWarehouseShoppingListBuffer } from "../../../../../contexts/WarehouseShoppingListBuffer";
import { ADD_WAREHOUSE_ITEM, CREATE_PRICE, VIEW_SERVICES } from "../../../../../enums/PermissionTitles";
import useUser from "../../../../../hooks/useUser";
import { useIsSmallerScreen } from "../../../../../utils/Functions";
import WarehouseCategoriesTab from "./components/WarehouseCategoriesTab";
import WarehouseItemsTable from "./components/WarehouseItemsTable";
import WarehouseSuppliersTab from "./components/WarehouseSuppliersTab";
import WarehouseServicesTab from "./components/services/WarehouseServicesTab";

export const WAREHOUSE_TABLE_PARAMS = {
  search: "search",
  category: "categoryId",
  supplier: "supplierId",
  sort: "sort",
  page: "page",
  size: "size",
  view: "view",
};

export const WAREHOUSE_VIEWS = {
  ITEMS: "items",
  CATEGORIES: "categories",
  SUPPLIERS: "suppliers",
  SERVICES: "services",
};

export default function ClinicWarehousePage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { roles, currentClinic } = useUser();

  const clinicId = roles.master ? id : currentClinic.id;

  const { pruneToClinicId } = useWarehouseLabelBuffer();
  const { pruneToClinicId: pruneShoppingListToClinicId } = useWarehouseShoppingListBuffer();
  React.useEffect(() => {
    if (clinicId) {
      pruneToClinicId(Number(clinicId));
      pruneShoppingListToClinicId(Number(clinicId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinicId]);

  const isSmallScreen = useIsSmallerScreen();
  const { isPermissionAllowed } = React.useContext(GlobalDataContext);
  const isAddingItemsAllowed = isPermissionAllowed(ADD_WAREHOUSE_ITEM);
  const isViewingServicesAllowed = isPermissionAllowed(VIEW_SERVICES);
  const isCreatingServicesAllowed = isPermissionAllowed(CREATE_PRICE);

  // Usluge is the default landing tab, but only for callers who can
  // actually see it - Items has no view-permission gate of its own, so it
  // stays the fallback for anyone without VIEW_SERVICES.
  const defaultView = isViewingServicesAllowed
    ? WAREHOUSE_VIEWS.SERVICES
    : WAREHOUSE_VIEWS.ITEMS;
  const activeView = searchParams.get(WAREHOUSE_TABLE_PARAMS.view) || defaultView;

  const itemsTableRef = React.useRef(null);
  const categoriesTabRef = React.useRef(null);
  const suppliersTabRef = React.useRef(null);
  const servicesTabRef = React.useRef(null);

  // Every tab exposes openAddDialog() via useImperativeHandle (the same
  // pattern WarehouseItemsTable already used) so this one header button
  // can trigger whichever tab is active, instead of each tab owning its
  // own separately-positioned Add button.
  const addButtonConfig = {
    [WAREHOUSE_VIEWS.SERVICES]: {
      ref: servicesTabRef,
      label: "AddService",
      visible: isViewingServicesAllowed && isCreatingServicesAllowed,
    },
    [WAREHOUSE_VIEWS.ITEMS]: {
      ref: itemsTableRef,
      label: "AddWarehouseItem",
      visible: isAddingItemsAllowed,
    },
    [WAREHOUSE_VIEWS.CATEGORIES]: {
      ref: categoriesTabRef,
      label: "AddWarehouseCategory",
      visible: true,
    },
    [WAREHOUSE_VIEWS.SUPPLIERS]: {
      ref: suppliersTabRef,
      label: "AddWarehouseSupplier",
      visible: true,
    },
  };
  const activeAddButton = addButtonConfig[activeView];

  function handleViewChange(event, newView) {
    const params = new URLSearchParams(searchParams);
    if (newView === defaultView) {
      params.delete(WAREHOUSE_TABLE_PARAMS.view);
    } else {
      params.set(WAREHOUSE_TABLE_PARAMS.view, newView);
    }
    setSearchParams(params);
  }

  return (
    <PageContentWrapper sideMargin={isSmallScreen ? 5 : 25} noSideMargin>
      <MUI.Stack
        direction="row"
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        sx={{ mb: 3, gap: 2, flexWrap: "wrap" }}
      >
        <MUI.Box>
          <MUI.Typography variant="h5" sx={{ fontWeight: 700 }}>
            {t("Warehouse")}
          </MUI.Typography>
          <MUI.Typography variant="body2" color="text.secondary">
            {t("WarehouseSubtitle")}
          </MUI.Typography>
        </MUI.Box>
        {activeAddButton?.visible && (
          <MUI.Button
            variant="contained"
            disableElevation
            startIcon={<AddIcon />}
            onClick={() => activeAddButton.ref.current?.openAddDialog()}
            sx={{ borderRadius: 2 }}
            data-testid="warehouse.add-button"
          >
            {t(activeAddButton.label)}
          </MUI.Button>
        )}
      </MUI.Stack>

      <MUI.Paper
        variant="outlined"
        sx={{
          borderRadius: 3,
          borderColor: "divider",
          mb: 3,
          overflow: "hidden",
        }}
      >
        <MUI.Tabs
  value={activeView}
  onChange={handleViewChange}
  variant="scrollable"
  scrollButtons="auto"
  allowScrollButtonsMobile
  sx={{
    px: 1,
    maxWidth: "100%",
    "& .MuiTabs-scroller": {
      overflow: "hidden !important",
    },
  }}
>
          {isViewingServicesAllowed && (
            <MUI.Tab
              label={t("ServicesTab")}
              title={t("ServicesTab")}
              value={WAREHOUSE_VIEWS.SERVICES}
              sx={{ textTransform: "none", fontWeight: 600 }}
              data-testid="warehouse.tabs.services"
            />
          )}
          <MUI.Tab
            label={t("ItemsTab")}
            title={t("ItemsTab")}
            value={WAREHOUSE_VIEWS.ITEMS}
            sx={{ textTransform: "none", fontWeight: 600 }}
            data-testid="warehouse.tabs.items"
          />
          <MUI.Tab
            label={t("Categories")}
            title={t("Categories")}
            value={WAREHOUSE_VIEWS.CATEGORIES}
            sx={{ textTransform: "none", fontWeight: 600 }}
            data-testid="warehouse.tabs.categories"
          />
          <MUI.Tab
            label={t("Suppliers")}
            title={t("Suppliers")}
            value={WAREHOUSE_VIEWS.SUPPLIERS}
            sx={{ textTransform: "none", fontWeight: 600 }}
            data-testid="warehouse.tabs.suppliers"
          />
        </MUI.Tabs>
      </MUI.Paper>

      {activeView === WAREHOUSE_VIEWS.SERVICES && isViewingServicesAllowed && (
        <WarehouseServicesTab ref={servicesTabRef} clinicId={clinicId} />
      )}

      {activeView === WAREHOUSE_VIEWS.ITEMS && (
        <WarehouseItemsTable ref={itemsTableRef} />
      )}

      {activeView === WAREHOUSE_VIEWS.CATEGORIES && (
        <WarehouseCategoriesTab ref={categoriesTabRef} clinicId={clinicId} />
      )}

      {activeView === WAREHOUSE_VIEWS.SUPPLIERS && (
        <WarehouseSuppliersTab ref={suppliersTabRef} clinicId={clinicId} />
      )}
    </PageContentWrapper>
  );
}